import React, { useEffect, useRef, useState, ReactNode } from 'react';

/**
 * Screen Reader Only Component
 * 
 * Renders content that is only visible to screen readers.
 * Useful for providing additional context or instructions.
 */
export const ScreenReaderOnly: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <span
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {children}
    </span>
  );
};

/**
 * Live Region Component
 * 
 * A React component that wraps content in an ARIA live region.
 * Updates to children will be announced to screen readers.
 */
interface LiveRegionProps {
  children: ReactNode;
  priority?: 'polite' | 'assertive';
  atomic?: boolean;
  relevant?: 'additions' | 'removals' | 'text' | 'all';
  role?: 'status' | 'alert' | 'log' | 'timer';
  visuallyHidden?: boolean;
}

export const LiveRegion: React.FC<LiveRegionProps> = ({
  children,
  priority = 'polite',
  atomic = true,
  relevant = 'all',
  role = 'status',
  visuallyHidden = false,
}) => {
  const hiddenStyle: React.CSSProperties = visuallyHidden ? {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  } : {};

  return (
    <div
      role={role}
      aria-live={priority}
      aria-atomic={atomic}
      aria-relevant={relevant}
      style={hiddenStyle}
    >
      {children}
    </div>
  );
};

/**
 * Announcement Provider Component
 * 
 * Provides a context for managing announcements throughout the app.
 * Place this at the root of your app.
 */
interface AnnouncementContextType {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  lastAnnouncement: string;
}

export const AnnouncementContext = React.createContext<AnnouncementContextType>({
  announce: () => {},
  lastAnnouncement: '',
});

interface AnnouncementProviderProps {
  children: ReactNode;
}

export const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({ children }) => {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const [lastAnnouncement, setLastAnnouncement] = useState('');
  const politeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const assertiveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    setLastAnnouncement(message);
    
    if (priority === 'assertive') {
      // Clear existing timeout
      if (assertiveTimeoutRef.current) {
        clearTimeout(assertiveTimeoutRef.current);
      }
      // Clear then set (ensures change is detected)
      setAssertiveMessage('');
      setTimeout(() => setAssertiveMessage(message), 50);
      // Clear after announcement
      assertiveTimeoutRef.current = setTimeout(() => {
        setAssertiveMessage('');
      }, 3000);
    } else {
      if (politeTimeoutRef.current) {
        clearTimeout(politeTimeoutRef.current);
      }
      setPoliteMessage('');
      setTimeout(() => setPoliteMessage(message), 50);
      politeTimeoutRef.current = setTimeout(() => {
        setPoliteMessage('');
      }, 3000);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (politeTimeoutRef.current) clearTimeout(politeTimeoutRef.current);
      if (assertiveTimeoutRef.current) clearTimeout(assertiveTimeoutRef.current);
    };
  }, []);

  return (
    <AnnouncementContext.Provider value={{ announce, lastAnnouncement }}>
      {children}
      
      {/* Polite Live Region */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {politeMessage}
      </div>
      
      {/* Assertive Live Region */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {assertiveMessage}
      </div>
    </AnnouncementContext.Provider>
  );
};

/**
 * Hook to use announcement context
 */
export const useAnnouncementContext = () => {
  const context = React.useContext(AnnouncementContext);
  if (!context) {
    throw new Error('useAnnouncementContext must be used within an AnnouncementProvider');
  }
  return context;
};

/**
 * Skip Link Component
 * 
 * Allows keyboard users to skip to main content.
 * Essential for accessibility.
 */
interface SkipLinkProps {
  targetId: string;
  children?: ReactNode;
}

export const SkipLink: React.FC<SkipLinkProps> = ({ 
  targetId, 
  children = 'Skip to main content' 
}) => {
  return (
    <a
      href={`#${targetId}`}
      className="skip-link"
      style={{
        position: 'absolute',
        left: '-9999px',
        zIndex: 9999,
        padding: '1rem',
        backgroundColor: '#fff',
        color: '#000',
        textDecoration: 'none',
        fontWeight: 'bold',
      }}
      onFocus={(e) => {
        e.currentTarget.style.left = '50%';
        e.currentTarget.style.transform = 'translateX(-50%)';
        e.currentTarget.style.top = '1rem';
      }}
      onBlur={(e) => {
        e.currentTarget.style.left = '-9999px';
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.top = 'auto';
      }}
    >
      {children}
    </a>
  );
};

/**
 * Focus Trap Component
 * 
 * Traps focus within a container for modals and dialogs.
 */
interface FocusTrapProps {
  children: ReactNode;
  active?: boolean;
  onEscape?: () => void;
}

export const FocusTrap: React.FC<FocusTrapProps> = ({ 
  children, 
  active = true,
  onEscape 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLElement | null>(null);
  const lastFocusableRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    const focusableElements = containerRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    firstFocusableRef.current = focusableElements[0];
    lastFocusableRef.current = focusableElements[focusableElements.length - 1];

    // Focus first element
    firstFocusableRef.current.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        onEscape();
        return;
      }

      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusableRef.current) {
          e.preventDefault();
          lastFocusableRef.current?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusableRef.current) {
          e.preventDefault();
          firstFocusableRef.current?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, onEscape]);

  return (
    <div ref={containerRef} role="dialog" aria-modal="true">
      {children}
    </div>
  );
};

/**
 * Visually Hidden Input Label
 * 
 * For inputs that have visible labels elsewhere but need
 * accessible labels for screen readers.
 */
export const HiddenLabel: React.FC<{ 
  htmlFor: string; 
  children: ReactNode 
}> = ({ htmlFor, children }) => {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {children}
    </label>
  );
};

export default {
  ScreenReaderOnly,
  LiveRegion,
  AnnouncementProvider,
  useAnnouncementContext,
  SkipLink,
  FocusTrap,
  HiddenLabel,
};
